import { Room } from '@/models';
import { getAllEntitiesByCriteria } from '@/store/entities/helper';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { Entity } from '@/models/types/Entity';
import { DeviceClasses } from '@/models/devices/Device';
import { Tag } from '@/utils/canvas/drawingUtils';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { Store } from '@/store/pinia-class-component';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { miniEntityStore } from '@/store/entities/types/miniEntityStore';
import { useFunkcheckStore } from '@/store/funkcheck/funkcheckStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { NO_SIGNAL_ICON } from '@/enums/icons';
import { usePlantStore } from '@/store/entities/plantStore';
import { PlantDataClasses } from '@/models/plant/Plant';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useOrderStore } from '@/store/order/orderStore';
import { deviceTypes } from '@/enums/device';

export function useRoomStore() {
  return new RoomStore();
}

@Store
export class RoomStore extends miniEntityStore {
  private _rooms: Map<string, Room>;
  private _byRoomGroupIdCache: Map<string, Room[]> | null = null;

  constructor() {
    super();
    this._rooms = new Map();
  }

  private getByRoomGroupIdIndex(): Map<string, Room[]> {
    if (!this._byRoomGroupIdCache) {
      const tempIndex = new Map<string, Room[]>();
      this._rooms.forEach((room) => {
        if (room.roomGroupId) {
          const list = tempIndex.get(room.roomGroupId) || [];
          list.push(room);
          tempIndex.set(room.roomGroupId, list);
        }
      });
      this._byRoomGroupIdCache = tempIndex;
    }
    return this._byRoomGroupIdCache;
  }

  private invalidateRoomGroupIdCache(): void {
    this._byRoomGroupIdCache = null;
  }

  get rooms(): Map<string, Room> {
    return this._rooms;
  }

  getStores() {
    const deviceStore = useDeviceStore();
    const entitiesStore = useEntitiesStore();
    const roomGroupStore = useRoomGroupStore();
    const installationPointStore = useInstallationPointStore();
    const inspectionPointStore = useInspectionPointStore();
    const layoutStore = useLayoutStore();
    const plantStore = usePlantStore();
    const orderStore = useOrderStore();

    return {
      deviceStore,
      entitiesStore,
      roomGroupStore,
      layoutStore,
      inspectionPointStore,
      installationPointStore,
      plantStore,
      orderStore,
    };
  }

  addEntities(entities: Room[]): void {
    this._rooms = new Map(
      entities.map((entity) => [entity.id, entity] as [string, Room])
    );
    this.invalidateRoomGroupIdCache();
  }

  addEntity(newRoom: Room): void {
    this._rooms.set(newRoom.id, newRoom);
    this.invalidateRoomGroupIdCache();
  }

  resetState() {
    this._rooms = new Map();
    this.invalidateRoomGroupIdCache();
  }

  getRoomsByRoomGroupId(roomGroupId: string): Array<Room> {
    return this.getByRoomGroupIdIndex().get(roomGroupId) || [];
  }

  getChildrenOfRoom(roomId: string): Array<Room> {
    return getAllEntitiesByCriteria(
      this._rooms,
      (room: Room) => room.parentRoomId === roomId
    );
  }

  getFirstRoomOfRoomgroup(roomgroupId: string): Room | undefined {
    const roomsOfRoomgroup = this.getRoomsByRoomGroupId(roomgroupId);
    // Find the room whose parent belongs to a different roomgroup
    return roomsOfRoomgroup.find(
      (room) =>
        (
          this.getStores().entitiesStore.getEntityById(
            room.getParentId() as string
          ) as Room
        ).roomGroupId !== roomgroupId
    );
  }

  getNewRoomOrdinal(parentId: string | undefined): number {
    if (parentId === undefined) {
      return 0;
    }
    const children = getAllEntitiesByCriteria(
      this._rooms,
      (room: Room) => room.getParentId() === parentId
    );
    return children.length;
  }

  getNotAccessibleHeaterRooms(): Array<Room> {
    return getAllEntitiesByCriteria(
      this._rooms,
      (room: Room) => room.usageType === 'HEATER' && !room.isAccessible
    );
  }

  getRoomBadgeIcon(roomId: string): Array<string> {
    const room = this._rooms.get(roomId);
    const notificationIcons = [];

    let roomHasNotDevice = false;

    const allInstallationPoints =
      this.getStores().installationPointStore.getInstallationPointsByRoomId(
        roomId
      );
    roomHasNotDevice = allInstallationPoints.some(
      (installationPoint) => installationPoint.failedInstallationAttempt
    );
    if (roomHasNotDevice || !room?.isAccessible)
      notificationIcons.push('mdi-alert-circle');

    const hasFunkcheckError =
      useFunkcheckStore().funkcheckErrors.filter((error) => {
        return error.roomId === room?.id;
      }).length > 0;

    if (hasFunkcheckError) notificationIcons.push(NO_SIGNAL_ICON);

    return notificationIcons;
  }

  getDeviceAndPlantTagsOfRoom(roomId: string): Array<Tag> {
    const tags: Array<Tag> = [];

    const inspectionPoints = this.getStores()
      .inspectionPointStore.getInspectionPointsByParentId(roomId)
      .filter((ip) => ip.activeTestDeviceId);

    const adapterInspectionPoints = this.getStores()
      .inspectionPointStore.getInspectionPointsByParentId(roomId)
      .filter((ip) => ip.isInspectionPointTypeAdapter());

    const inspectionPointStatus =
      useInspectionPointStore().getInspectionPointStatus({
        roomId,
      });

    const devices = this.getStores().deviceStore.getAllDevicesInRoom(roomId);
    const plants = this.getStores().plantStore.getAllPlantsInRoom(roomId);

    devices.filter(
      (device: DeviceClasses) => device.getLabel() !== 'Gerätetyp'
    );

    const AllEntities = [...devices, ...plants, ...inspectionPoints];

    const labels = new Set(
      AllEntities.map(
        (entity: DeviceClasses | PlantDataClasses | InspectionPoint) => {
          return entity.getLabel();
        }
      )
    );

    adapterInspectionPoints.forEach((adapterInspectionPoint) => {
      if (
        adapterInspectionPoint.activeTestDeviceId &&
        !adapterInspectionPoint.failedInstallationAttempt
      ) {
        return;
      }
      tags.push(
        new Tag(
          adapterInspectionPoint.getLabel(),
          adapterInspectionPoint.accessories.length,
          adapterInspectionPoint.getTagColor(),
          adapterInspectionPoint.getAdvancedTagStyling()
        )
      );
    });

    labels.forEach((label) => {
      const devicesByLabel = AllEntities.filter(
        (entity: DeviceClasses | PlantDataClasses | InspectionPoint) =>
          entity.getLabel() === label
      );
      const color = devicesByLabel[0].getTagColor();
      let customStyling = undefined;
      if (devicesByLabel[0] instanceof InspectionPoint) {
        customStyling = devicesByLabel[0].getAdvancedTagStyling(
          inspectionPointStatus
        );
      }
      tags.push(new Tag(label, devicesByLabel.length, color, customStyling));
    });
    return tags;
  }

  getRoomById(roomId: string): Room {
    const room = this._rooms.get(roomId);
    if (room === undefined) {
      throw new Error('Room not found');
    }
    return room;
  }

  getActiveAndFailedSrtDevices(roomId: string) {
    const srtDevices = this.getStores().deviceStore.getSrtDevicesInRoom(roomId);
    const failedSrtInstallationAttempts =
      this.getStores().installationPointStore.getFailedSrtInstallationAttempt(
        roomId
      );

    return [...srtDevices, ...failedSrtInstallationAttempts];
  }

  isFinished(roomId: string) {
    const hasRadiator = this.hasRadiator(roomId);
    const room = this.getRoomById(roomId);
    const { orderStore } = this.getStores();

    if (!room.isAccessible) {
      return true;
    }

    if (hasRadiator === false) {
      return true;
    }

    if (orderStore?.isOnSiteInspection()) {
      return this.hasAdapterInspectionData(roomId);
    }

    return (
      !!hasRadiator && this.getActiveAndFailedSrtDevices(roomId).length > 0
    );
  }

  private hasAdapterInspectionData(roomId: string) {
    const inspectionPoints = this.getStores()
      .inspectionPointStore.getInspectionPointsByParentId(roomId)
      .filter((ip) => {
        return (
          ip.supportedDeviceType === deviceTypes.SmartRadiatorThermostat ||
          ip.isInspectionPointTypeAdapter()
        );
      });

    return inspectionPoints.some((inspectionPoint) => {
      const hasActiveDevice = !!inspectionPoint.activeTestDeviceId;
      const hasAccessories =
        inspectionPoint.accessories?.some((adapter) => !adapter.unmountedAt) ??
        false;
      const noAdapterNeeded = !!inspectionPoint.inspectionData?.noAdapterNeeded;
      const hasFailedAttempt = !!inspectionPoint.failedInstallationAttempt;
      return (
        hasActiveDevice || hasAccessories || noAdapterNeeded || hasFailedAttempt
      );
    });
  }

  isIncomplete(roomId: string) {
    return (
      this.hasRadiator(roomId) === undefined &&
      this.getRoomById(roomId).isAccessible
    );
  }

  hasRadiator(roomId: string): boolean | undefined {
    const room = this.getRoomById(roomId);
    return room.hasRadiator;
  }

  getLeftSibling(entity: Room) {
    return getAllEntitiesByCriteria(
      this._rooms,
      (room: Room) =>
        room.parentRoomId === entity.parentRoomId &&
        room.ordinal === entity.ordinal - 1
    )[0];
  }

  swapRoom(entity: Room) {
    if (this._rooms.get(entity.id) === undefined) {
      throw new Error('Room not found');
    }

    // get sibling to be swapped
    const sibling = getAllEntitiesByCriteria(
      this._rooms,
      (room: Room) =>
        room.parentRoomId === entity.parentRoomId &&
        room.ordinal === entity.ordinal - 1
    )[0];

    // rooms whose ordinals should be swapped
    const tempOrdinal = entity.ordinal;
    entity.ordinal = sibling.ordinal;
    sibling.ordinal = tempOrdinal;

    this.getStores().layoutStore.changeCurrentNode(
      this.getStores().entitiesStore.getParentOfEntity(entity.id) as Entity
    );

    this.getStores().entitiesStore.saveEntity(sibling);
    this.getStores().entitiesStore.saveEntity(entity);
  }

  setRoomHasRadiator(roomId, hasRadiator) {
    const room = this.getRoomById(roomId);
    const updatedRoom = new Room();
    Object.assign(updatedRoom, room);
    updatedRoom.hasRadiator = hasRadiator;
    this.getStores().entitiesStore.saveEntity(updatedRoom);
  }

  isDeletable(room: Room): boolean {
    return (
      useEntitiesStore().getAllEntitiesByParentId(room.id).length === 0 &&
      room.roomGroupId !== undefined &&
      this.getFirstRoomOfRoomgroup(room.roomGroupId)?.id !== room.id
    );
  }

  deleteEntity(entity: Room) {
    this._rooms.delete(entity.id);
    this.invalidateRoomGroupIdCache();
  }
}
